import { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { MdContentCopy } from '@react-icons/all-files/md/MdContentCopy';
import { MdOutlineAccessTime } from '@react-icons/all-files/md/MdOutlineAccessTime';
import moment from 'moment';
import { fetchSsoSetupLink, generateSsoSetupLink, SsoSetupLink } from 'Apis/library';
import { ToastsContext } from 'Rdx/contexts/ToastsContext';
import { ERROR_VARIANT, SUCCESS_VARIANT } from 'Core-utils/constants/constants';

const NOT_PROVISIONED_MESSAGE =
    'SSO is not set up for this tenant yet. Contact Amplifier to enable SSO before generating a setup link.';
const GENERIC_ERROR_MESSAGE = 'Could not generate an SSO setup link. Please try again.';

/**
 * Tenant-admin SSO setup link section. Renders inside the Users & Access tab.
 * Fetches any active link on mount, lets an admin generate/regenerate one, and
 * shows the link with its expiration until it lapses. The backend endpoint is
 * role-gated; this section relies on the enclosing tab for visibility.
 */
export const SsoSetupSection = () => {
    const { addToast } = useContext(ToastsContext);
    const [loading, setLoading] = useState(true);
    const [provisioned, setProvisioned] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [setupLink, setSetupLink] = useState<SsoSetupLink | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const result = await fetchSsoSetupLink();
                if (active) {
                    setProvisioned(Boolean(result?.provisioned));
                    if (result?.link) {
                        setSetupLink(result);
                    }
                }
            } catch {
                // Treat a failed status check as not provisioned so the section stays hidden.
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            active = false;
        };
    }, []);

    const handleGenerate = useCallback(async () => {
        setGenerating(true);
        setError(null);
        try {
            const result = await generateSsoSetupLink();
            setSetupLink(result);
            addToast({ severity: SUCCESS_VARIANT, body: 'SSO setup link generated.' });
        } catch (err) {
            const status = (err as { response?: { status?: number } })?.response?.status;
            const message = status === 409 ? NOT_PROVISIONED_MESSAGE : GENERIC_ERROR_MESSAGE;
            setError(message);
            addToast({ severity: ERROR_VARIANT, body: message });
        } finally {
            setGenerating(false);
        }
    }, [addToast]);

    const handleCopy = useCallback(async () => {
        if (!setupLink?.link) {
            return;
        }
        try {
            await navigator.clipboard.writeText(setupLink.link);
            addToast({ severity: SUCCESS_VARIANT, body: 'Link copied to clipboard.' });
        } catch {
            addToast({ severity: ERROR_VARIANT, body: 'Could not copy the link.' });
        }
    }, [setupLink, addToast]);

    const activeLink = setupLink?.link ? setupLink : null;

    // Hide the section entirely until we confirm the tenant is provisioned in Descope.
    // Rendering nothing while loading avoids flashing the section for unprovisioned tenants.
    if (loading || !provisioned) {
        return null;
    }

    return (
        <div className="settings-v2__card settings-v2__sso">
            <div className="settings-v2__card-header">
                <h3 className="settings-v2__card-title">Single sign-on</h3>
                <p className="settings-v2__card-subtitle">
                    Generate a setup link your identity provider administrator can use to configure SSO. The link stays
                    available here until it expires.
                </p>
            </div>

            <div className="settings-v2__sso-body">
                {activeLink && (
                    <>
                        <div className="settings-v2__sso-field">
                            <span className="settings-v2__sso-field-label">SSO Configuration Link</span>
                            <input
                                className="settings-v2__sso-link-input"
                                type="text"
                                readOnly
                                value={activeLink.link ?? ''}
                                aria-label="SSO setup link"
                                onFocus={(e) => e.currentTarget.select()}
                            />
                            <button
                                type="button"
                                className="settings-v2__sso-copy"
                                onClick={handleCopy}
                                aria-label="Copy SSO setup link"
                            >
                                <MdContentCopy size={16} />
                            </button>
                        </div>
                        <p className="settings-v2__sso-expiry">
                            <MdOutlineAccessTime size={14} />
                            <span>
                                Expires {moment(activeLink.expiresAt).fromNow()} (
                                {moment(activeLink.expiresAt).format('MMM D, YYYY h:mm A')})
                            </span>
                        </p>
                    </>
                )}

                {error && <p className="settings-v2__sso-error">{error}</p>}

                <Button
                    className="settings-v2__sso-generate"
                    variant="primary"
                    onClick={handleGenerate}
                    disabled={generating}
                >
                    {generating ? (
                        <>
                            <Spinner animation="border" size="sm" /> Generating…
                        </>
                    ) : activeLink ? (
                        'Regenerate link'
                    ) : (
                        'Generate SSO setup link'
                    )}
                </Button>
            </div>
        </div>
    );
};
